import { Injectable, signal } from '@angular/core';
import { createAppKit } from '@reown/appkit';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { Config, connect, disconnect, getAccount, switchChain, watchAccount } from '@wagmi/core';
import { defineChain } from 'viem';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AppKitService {

    private wagmiAdapter!: WagmiAdapter;
    private wagmiConfig!: Config;
    private appKit!: any;
    private isSwitching = false;
    private projectId = environment.PROJECT_ID;
    private initPromise: Promise<void>;
    public walletState = signal<any>({});
    public isConnecting = signal(false);

    constructor() {
        this.initPromise = this.initializeAppKit();
    }

    //  FIXED: Define Amoy manually (IMPORTANT)
    private supportedNetworks: any = [
        defineChain({
            id: 80002,
            name: 'Polygon Amoy',
            network: 'polygon-amoy',
            nativeCurrency: {
                name: 'POL',
                symbol: 'POL',
                decimals: 18
            },
            rpcUrls: {
                default: {
                    http: ['https://rpc-amoy.polygon.technology']
                }
            },
            blockExplorers: {
                default: {
                    name: 'PolygonScan',
                    url: 'https://amoy.polygonscan.com'
                }
            }
        })
    ];



    // WAIT FOR INIT
    async waitForInitialization(): Promise<void> {
        return this.initPromise;
    }

    //  GET CONFIG
    getWagmiConfig(): Config {
        return this.wagmiConfig;
    }

    //  INIT
    private async initializeAppKit() {
        try {
            this.wagmiAdapter = new WagmiAdapter({
                projectId: this.projectId,
                networks: this.supportedNetworks,
                ssr: false
            });

            this.wagmiConfig = this.wagmiAdapter.wagmiConfig as Config;

            this.appKit = createAppKit({
                adapters: [this.wagmiAdapter],
                networks: this.supportedNetworks,
                defaultNetwork: this.supportedNetworks[0],
                projectId: this.projectId,
                enableNetworkSwitch: false,
                features: {
                    analytics: false,
                    email: false,
                    socials: [],
                    onramp: false,
                    swaps: false
                },
                allWallets: 'HIDE'
            });

            // Sync initial state
            const account = getAccount(this.wagmiConfig);
            this.updateState(account);

            // Watch for changes
            watchAccount(this.wagmiConfig, {
                onChange: (account) => {
                    this.updateState(account);

                    //  ONLY act when wagmi is ready
                    if (account.isConnected !== undefined) {
                        //  If already on Amoy → close modal ONCE
                        if (account.isConnected && Number(account.chainId) === 80002) {
                            setTimeout(() => {
                                this.appKit?.close();
                            }, 300); // small safe delay
                        }
                    }
                }
            });

        } catch (error) {
            console.error('AppKit init error:', error);
        }
    }

    //  UPDATE STATE
    private updateState(account: any) {
        this.walletState.set({
            isConnected: account.isConnected,
            address: account.address,
            chainId: account.chainId,
            network: account.chain?.name,
            isSupportedNetwork: Number(account.chainId) === 80002
        });
    }

    //  DISCONNECT
    async disconnect() {
        await this.waitForInitialization();
        try {
            await disconnect(this.wagmiConfig);
        } catch (error) {
            console.error('Error disconnecting:', error);
        }
    }

    // GET CURRENT STATE
    getCurrentWalletState() {
        return this.walletState();
    }

    // WAIT FOR WALLET
    private async waitForWalletConnection(): Promise<void> {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                const account = getAccount(this.wagmiConfig);
                if (account?.address) {
                    clearInterval(interval);
                    resolve();
                }
            }, 300);
        });
    }

    // 🔹 CHECK CHAIN
    isOnChain(requiredChainId: number): boolean {
        const account = getAccount(this.wagmiConfig);
        return Number(account.chainId) === Number(requiredChainId);
    }

    // 🔹 CONNECT WALLET
    async connectWallet(connectorId?: string): Promise<any> {
        await this.waitForInitialization();
        try {
            // 🔸 MetaMask direct connect
            if (connectorId === 'metaMask') {
                const currentAccount = getAccount(this.wagmiConfig);
                if (currentAccount.isConnected && currentAccount.address) {
                    return currentAccount;
                }

                const connectors = this.wagmiConfig.connectors;

                const connector = connectors.find(
                    c =>
                        c.id === 'io.metamask' ||
                        c.name === 'MetaMask' ||
                        (c as any)._isMetaMask
                );

                if (connector) {
                    this.setWalletConnecting(true);

                    await connect(this.wagmiConfig, { connector });

                    await this.waitForWalletConnection();

                    const account = getAccount(this.wagmiConfig);
                    if (Number(account.chainId) !== 80002) {
                        try {
                            await switchChain(this.wagmiConfig, {
                                chainId: 80002
                            });
                        } catch (e) {
                            console.log('User rejected switch');
                        }
                    }
                    this.setWalletConnecting(false);
                    return account;
                }
            }

            //  AppKit modal connect - check state first
            const state = getAccount(this.wagmiConfig);
            if (state.isConnected) {
                await this.appKit.open({ view: 'Account' });
            } else {
                await this.appKit.open({ view: 'Connect' });
            }
            this.setWalletConnecting(true);

            await this.waitForWalletConnection();

            const account = getAccount(this.wagmiConfig);
            this.validateConnectedAccount(account.address);
            this.setWalletConnecting(false);
            return account;

        } catch (error) {
            console.error('Error connecting wallet:', error);
            this.setWalletConnecting(false);
            
            const account = getAccount(this.wagmiConfig);
            const errorName = (error as any)?.name || '';
            const errorMessage = ((error as any)?.message || '').toLowerCase();

            // If it's just an "already connected" error, treat as success
            if (
                account.isConnected &&
                account.address &&
                (errorName === 'ConnectorAlreadyConnectedError' || errorMessage.includes('already connected'))
            ) {
                this.validateConnectedAccount(account.address);
                return account;
            }

            throw error; // Rethrow to allow UI to handle specific error codes (like -32002)
        }
    }

    private validateConnectedAccount(newAddress?: string): void {
        if (!newAddress) return;

        try {
            const userDataStr = localStorage.getItem('usr_meta'); // STORAGE_KEYS.USER_META_INFO
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                const loggedInAddress = userData?.public_address || userData?.publicAddress;
                
                if (loggedInAddress && newAddress.toLowerCase() !== loggedInAddress.toLowerCase()) {
                    throw new Error("Account mismatch: The connected wallet does not match the logged-in user.");
                }
            }
        } catch (e) {
            if (e instanceof Error && e.message.includes("Account mismatch")) {
                throw e;
            }
            console.error("Validation error in AppKit:", e);
        }
    }

    //  HELPER TO MANAGE LOADING STATE
    private setWalletConnecting(state: boolean) {
        this.isConnecting.set(state);
    }
}
